import React, { useEffect, useState } from 'react';
import { ReturnKeyTypeOptions, StyleProp, TextStyle } from 'react-native';
import { TextInput } from 'react-native-paper';

interface OperatorLicenseInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  onBlur?: () => void;
  error?: boolean;
  style?: StyleProp<TextStyle>;
  testID?: string;
  /** Lets the chat flow advance on Enter / the keyboard action key. */
  onSubmitEditing?: () => void;
  returnKeyType?: ReturnKeyTypeOptions;
}

const applyMaskOperatorLicense = (value: string): string => {
  // Keep only digits to drive formatting
  const digitsOnly = value.replace(/\D/g, '');

  // If 4 or fewer digits, show as-is (no slash yet)
  if (digitsOnly.length <= 4) {
    return digitsOnly;
  }

  // Always format as [leading digits]/[last 4 digits]
  const yearPart = digitsOnly.slice(-4);
  const numberPart = digitsOnly.slice(0, -4);

  return `${numberPart}/${yearPart}`;
};

export const OperatorLicenseInput: React.FC<OperatorLicenseInputProps> = ({
  label,
  value,
  onChangeText,
  onBlur,
  error,
  style,
  testID,
  onSubmitEditing,
  returnKeyType,
}) => {
  const handleChange = (text: string) => {
    const masked = applyMaskOperatorLicense(text);
    onChangeText(masked);
  };

  const [isInvalidYear, setIsInvalidYear] = useState(false);

  const validateYear = (licenseValue: string) => {
    // Expect format: up to 8 digits, '/', 4 digits (year)
    const parts = (licenseValue || '').split('/');
    if (parts.length === 2) {
      const yearPart = parts[1];
      // Only validate when 4 digits are present
      if (yearPart && yearPart.length === 4) {
        const yearNum = Number(yearPart);
        const currentYear = new Date().getFullYear();
        // Allow years from 1900 to currentYear + 5 (matching backend validation)
        return !(Number.isInteger(yearNum) && yearNum >= 1900 && yearNum <= currentYear + 5);
      }
    }
    return false;
  };

  useEffect(() => {
    setIsInvalidYear(validateYear(value));
  }, [value]);

  return (
    <TextInput
      label={label}
      mode="outlined"
      keyboardType="numeric"
      onBlur={onBlur}
      onChangeText={handleChange}
      value={value}
      error={!!error || isInvalidYear}
      style={style}
      maxLength={20}
      testID={testID}
      onSubmitEditing={onSubmitEditing}
      returnKeyType={returnKeyType}
    />
  );
};

export default OperatorLicenseInput;


